package org.zest.insan_360_website.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.dom.svg.Line
import com.varabyte.kobweb.compose.dom.svg.Polygon
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.dom.svg.Text
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.graphics.lightened
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.ElementScope
import org.w3c.dom.svg.SVGSVGElement
import org.zest.insan_360_website.components.layouts.containerShadowStyle
import org.zest.insan_360_website.components.layouts.resultWordLibrary
import org.zest.insan_360_website.components.layouts.userPageBorderStrongColor
import org.zest.insan_360_website.components.layouts.userPageColor
import org.zest.insan_360_website.data.GraphFormat
import org.zest.insan_360_website.data.PlotPoint
import org.zest.insan_360_website.data.page_states.UserPreferences
import org.zest.insan_360_website.model.Attribute
import org.zest.insan_360_website.model.GraphType
import org.zest.insan_360_website.pages.Answer
import org.zest.insan_360_website.pages.color

data class Measurements(
    val width: Int,
    val height: Int,
    val typeHeight: Int,
    val labelHeight: Int,
    val percentHeight: Int,
    val horizontalGap: Int,
    val verticalGap: Int,
    val scale: Int = 4
) {
    fun addScale(newScale: Int): Measurements {
        return this.copy(scale = newScale)
    }

    fun width() = width * scale
    fun height() = height * scale
    fun typeHeight() = typeHeight * scale
    fun labelHeight() = labelHeight * scale
    fun percentHeight() = percentHeight * scale
    fun horizontalGap() = horizontalGap * scale
//    fun verticalGap() = verticalGap * scale
    fun totalHeight() = typeHeight() + labelHeight() + height() + percentHeight()
}

fun processData(answers: List<Answer>): Triple<MutableMap<Attribute, Int>, MutableMap<Attribute, Int>, Map<Attribute, Int>> {
    val plotPointsMore = Attribute.entries.associateWith { 0 }.toMutableMap()
    val plotPointsLess = Attribute.entries.associateWith { 0 }.toMutableMap()

    answers.forEach { answer ->
        val moreAttr = answer.moreStatement?.moreAttr
        val lessAttr = answer.lessStatement?.lessAttr
        if (moreAttr != null) {
            plotPointsMore[moreAttr] = plotPointsMore.getValue(moreAttr) + 1
        }
        if (lessAttr != null) {
            plotPointsLess[lessAttr] = plotPointsLess.getValue(lessAttr) + 1
        }
    }
    plotPointsMore.remove(Attribute.Dummy)
    plotPointsLess.remove(Attribute.Dummy)

    val plotPointsDifference = Attribute.entries
        .filter { it != Attribute.Dummy }
        .associateWith { attr -> plotPointsMore.getValue(attr) - plotPointsLess.getValue(attr) }

    return Triple(plotPointsMore, plotPointsLess, plotPointsDifference)
}

@Composable
fun DiscGraphs(
    modifier: Modifier = Modifier,
    measurements: Measurements,
    plotPointsMore: Map<Attribute, Int>,
    plotPointsLess: Map<Attribute, Int>,
    plotPointsDifference: Map<Attribute, Int>,
    isForPrint: Boolean = false
) {
    val paddingPx = 8
    val printWidth = (measurements.width() * 3) + (measurements.horizontalGap() * 2) + paddingPx
    val printHeight = measurements.totalHeight() + paddingPx

    Row(
        modifier = Modifier
            .id(if (isForPrint) "disc-graphs-print" else "disc-graphs")
            .padding(4.px)
            .justifyContent(JustifyContent.Center)
            .gap(measurements.horizontalGap().px)
            .thenIf(isForPrint) {
                Modifier
                    .width(printWidth.px)
                    .height(printHeight.px)
                    .minWidth(printWidth.px)
                    .flexShrink(0)
                    .flexWrap(FlexWrap.Nowrap)
                    .position(Position.Absolute)
                    .top((-9999).px)
                    .left((-9999).px)
            }
            .thenIf(!isForPrint) {
                Modifier
                    .fillMaxWidth()
                    .flexWrap(FlexWrap.Wrap)
            }
            .then(modifier)
    ) {
        val discModifier = if (isForPrint) {
            Modifier
                .width(measurements.width().px)
                .height(measurements.totalHeight().px)
                .flexShrink(0)
        } else {
            val padding = 1.cssRem
            Modifier
                .minWidth(measurements.width().px + padding*2.25)
                .height(measurements.totalHeight().px + padding*2)
                .padding(padding)
                .backgroundColor(userPageColor)
                .border {
                    width(1.px)
                    style(LineStyle.Solid)
                    color(userPageBorderStrongColor)
                }
                .borderRadius(1.cssRem)
                .then(containerShadowStyle.toModifier())
        }

        PlotDisc(
            modifier = discModifier,
            measurements = measurements,
            gridFormat = GraphFormat.GRAPH_FORMAT_MORE,
            type =  GraphType.More,
            plotPoints = plotPointsMore
        )
        PlotDisc(
            modifier = discModifier,
            measurements = measurements,
            gridFormat = GraphFormat.GRAPH_FORMAT_LESS,
            type = GraphType.Less,
            plotPoints = plotPointsLess
        )
        PlotDisc(
            modifier = discModifier,
            measurements = measurements,
            gridFormat = GraphFormat.GRAPH_FORMAT_DIFFERENCE,
            type = GraphType.Difference,
            plotPoints = plotPointsDifference
        )
    }
}

@Composable
fun PlotDisc(
    modifier: Modifier = Modifier,
    measurements: Measurements,
    gridFormat: List<PlotPoint>,
    type: GraphType,
    plotPoints: Map<Attribute, Int>? = null
) {
    val sortedPlotPoints = plotPoints?.entries?.sortedBy { it.key.ordinal }?.associate { it.key to it.value }
    val labelY =
        measurements.typeHeight()
    val gridY =
        measurements.typeHeight() +
                measurements.labelHeight()
    val percentY =
        measurements.typeHeight() +
                measurements.labelHeight() +
                measurements.height()

    Svg(
        modifier
            .toAttrs {
                attr("width", measurements.width().toString())
                attr("height", measurements.totalHeight().toString())
            }
    ) {
        val text =
            resultWordLibrary.graphLabel[type]?.get(UserPreferences.language) ?: when (type) {
                GraphType.More -> "PUBLIC SELF"
                GraphType.Less -> "PRIVATE SELF"
                GraphType.Difference -> "PERCEIVED SELF"
            }

        Text(text.uppercase()) {
            y(measurements.typeHeight())
            x(measurements.width() * 0.5)
            fontSize(8 * measurements.scale)
            fontWeight(FontWeight.Bold)
            attr("text-anchor", "middle")
        }
        Svg(
            Modifier
                .width(measurements.width().px)
                .height(measurements.labelHeight().px)
                .translate(tx = 0.px, ty = labelY.px)
                .toAttrs()
        ) {
            Attribute.entries.forEach { attr -> AttrLabel(measurements.width(), attr, measurements.scale) }
        }
        Svg(
            attrs = Modifier
                .alignItems(AlignItems.Center)
                .size(measurements.width().px, measurements.height().px)
                .border {
                    color(Colors.Black)
                    width(4.px)
                    style(LineStyle.Solid)
                }
                .translate(tx = 0.px, ty = gridY.px)
                .toAttrs()
        ) {
            GridAppearance(measurements.width(), measurements.height(), measurements.scale)

//            for (point in gridFormat) {
//                PlotPoint(
//                    point.number.toString(),
//                    point.interval,
//                    point.attr,
//                    type,
//                    measurements.height(),
//                    measurements.scale,
//                    measurements.width()
//                )
//            }

            if (!sortedPlotPoints.isNullOrEmpty()) {
                LinePlot(
                    sortedPlotPoints,
                    gridFormat,
                    type,
                    measurements.width(),
                    measurements.scale,
                    measurements.height()
                )
            }
        }
        if (!sortedPlotPoints.isNullOrEmpty()) {
            val centerYList = findCenter(sortedPlotPoints, gridFormat)
            Svg(
                Modifier
                    .width(measurements.width().px)
                    .height(measurements.percentHeight().px)
                    .translate(
                        tx = 0.px,
                        ty = percentY.px
                    )
                    .toAttrs()
            ) {
                sortedPlotPoints.toList().withIndex().forEach { (index, plotPoint) ->
                    val section = plotPoint.first.ordinal + 1

                    AttrPercentage(
                        measurements.width(),
                        measurements.height().toFloat() / measurements.scale,
                        if (type == GraphType.Less) {
                            (measurements.height() / measurements.scale) - centerYList[index].toFloat()
                        } else {
                            centerYList[index].toFloat()
                        },
                        section,
                        plotPoint.first.color,
                        measurements.scale
                    )
                }
            }
        }
    }
}

@Composable
fun ElementScope<SVGSVGElement>.LinePlot(
    plotPoints: Map<Attribute, Int>,
    gridFormat: List<PlotPoint>,
    type: GraphType,
    width: Int,
    scale: Int,
    height: Int
) {
    val centerYList = findCenter(plotPoints, gridFormat)

    for (i in 1 until centerYList.size) {
        Line {
            when (type) {
                GraphType.Less -> {
                    y1(centerYList[i - 1] * scale)
                    y2(centerYList[i] * scale)
                }

                else -> {
                    y1(height - centerYList[i - 1] * scale)
                    y2(height - centerYList[i] * scale)
                }
            }
            x1(width * i / 5)
            x2(width * (i + 1) / 5)
            stroke(Colors.Black)
            strokeWidth((scale).px)
        }
    }

    plotPoints.toList().withIndex().forEach { (index, point) ->
        val attr = point.first
        val adjustedScale = scale * 2.5
        val attrLine = attr.ordinal + 1
        Polygon {
            when (type) {
                GraphType.Less -> {
                    points(
                        ((width * attrLine / 5) - adjustedScale) to centerYList[index] * scale,
                        (width * attrLine / 5) to centerYList[index] * scale - adjustedScale,
                        ((width * attrLine / 5) + adjustedScale) to centerYList[index] * scale,
                        (width * attrLine / 5) to centerYList[index] * scale + adjustedScale
                    )
                }

                else -> {
                    points(
                        ((width * attrLine / 5) - adjustedScale) to height - centerYList[index] * scale,
                        (width * attrLine / 5) to height - centerYList[index] * scale - adjustedScale,
                        ((width * attrLine / 5) + adjustedScale) to height - centerYList[index] * scale,
                        (width * attrLine / 5) to height - centerYList[index] * scale + adjustedScale
                    )
                }
            }
            fill(attr.color)
        }
    }
}

fun findCenter(
    plotPoints: Map<Attribute, Int>,
    gridFormat: List<PlotPoint>
): MutableList<Int> {
    val centerYList = mutableListOf<Int>()
    plotPoints.toList().withIndex().forEach { (_, point) ->
        val attr = point.first
        val value = point.second

        centerYList.add(
            gridFormat
                .filter { it.attr == attr }
                .let { results ->
                    results.firstOrNull { it.number == value }?.interval
                        ?: if (value < 0) results.first { it.number > value }.interval
                        else results.last { it.number < value }.interval
                }
        )
    }
    return centerYList
}

@Composable
fun ElementScope<SVGSVGElement>.GridAppearance(
    width: Int,
    height: Int,
    scale: Int
) {
    val tenPercentLine = height * 1 / 10
    val middlePoint = height / 2

    Polygon {
        points(
            0 to 0,
            width to 0,
            width to height,
            0 to height
        )
        fill(Colors.White)
    }
    Polygon {
        points(
            0 to 0,
            width to 0,
            width to tenPercentLine,
            0 to tenPercentLine
        )
        fill(Colors.DarkSlateGray.lightened(0.1F))
    }
    Polygon {
        points(
            0 to height,
            width to height,
            width to height - tenPercentLine,
            0 to height - tenPercentLine
        )
        fill(Colors.DarkSlateGray.lightened(0.1F))
    }
    Polygon {
        points(
            0 to middlePoint + tenPercentLine,
            width to middlePoint + tenPercentLine,
            width to middlePoint - tenPercentLine,
            0 to middlePoint - tenPercentLine
        )
        fill(Colors.DarkGray)
    }
    Line {
        x1(0)
        y1(tenPercentLine)
        x2(width)
        y2(tenPercentLine)
        stroke(Colors.Black)
        strokeWidth((scale * 0.5).px)
        strokeDashArray((scale).px)
    }
    Line {
        x1(0)
        y1(middlePoint + tenPercentLine)
        x2(width)
        y2(middlePoint + tenPercentLine)
        stroke(Colors.Black)
        strokeWidth((scale * 0.5).px)
        strokeDashArray((scale).px)
    }
    Line {
        x1(0)
        y1(middlePoint - tenPercentLine)
        x2(width)
        y2(middlePoint - tenPercentLine)
        stroke(Colors.Black)
        strokeWidth((scale * 0.5).px)
        strokeDashArray((scale).px)
    }


    for (x in 0..width step (width / 5)) {
        Line {
            x1(x)
            y1(0)
            x2(x)
            y2(height)
            stroke(Colors.DarkSlateGray)
            strokeWidth(1.px)
        }
    }

    for (y in (height / 4)..(height * 3 / 4) step (height / 4)) {
        Line {
            x1(0)
            y1(y)
            x2(width)
            y2(y)
            stroke(Colors.DarkSlateGray)
            strokeWidth(2)
            strokeDashArray(8)
        }
    }

    Line {
        x1(0)
        y1(height / 2)
        x2(width)
        y2(height / 2)
        stroke(Colors.Black)
        strokeWidth((scale).px)
    }
}

@Composable
fun ElementScope<SVGSVGElement>.AttrLabel(
    width: Int,
    attr: Attribute,
    scale: Int
) {
    val text =
        when (attr) {
            Attribute.Dominance -> "D"
            Attribute.Influence -> "I"
            Attribute.Steadiness -> "S"
            Attribute.Compliance -> "C"
            Attribute.Dummy -> return
        }
    val section = attr.ordinal + 1

    Text(text) {
        y(8 * scale)
        x(width * section / 5)
        fontSize(7 * scale)
        fontWeight(FontWeight.Bold)
        fill(attr.color)
        attr("text-anchor", "middle")
    }
}

@Composable
fun ElementScope<SVGSVGElement>.AttrPercentage(
    width: Int,
    height: Float,
    centerY: Float,
    section: Int,
    color: Color,
    scale: Int
) {
    Text(
        ((centerY / height) * 100).toInt().toString() + "%"
    ) {
        y(8 * scale)
        x(width * section / 5)
        fontSize(6 * scale)
        fontWeight(FontWeight.Bold)
        fill(color)
        attr("text-anchor", "middle")
    }
}

@Composable
fun ElementScope<SVGSVGElement>.PlotPoint(
    text: String,
    interval: Int,
    attr: Attribute,
    type: GraphType,
    height: Int,
    scale: Int,
    width: Int
) {
    var text = text
    val attributeLine = attr.ordinal + 1
    when (type) {
        GraphType.Difference -> {
            val value = text.toIntOrNull() ?: 0
            text = when {
                value > 0 -> "+$text"
                value < 0 -> text
                else -> "0"
            }
        }

        else -> {}
    }
    Text(text) {
        when (type) {
            GraphType.Less -> {
                y(interval * scale)
            }

            else -> {
                y(height - interval * scale)
            }
        }
        x((width * attributeLine / 5) + scale)
        fontSize(3 * scale)
        fontWeight(FontWeight.Medium)
        attr("dominant-baseline", "central")
    }
}